import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {clearSelection} from 'src/components/data-table-selection';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {
    NoResultPage,
    PreparedEntitlementsView,
    ResourceSuggest,
    useExpandRoles,
    useOpenEntitlementDrawer,
} from 'src/modules/entitlements';
import {closeDrawer} from 'src/modules/entitlements/components/drawer/entitlement-drawer.slice';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {searchDatasetResources} from 'src/openapi/admin/apis/DatasetEntitlementsResourceSearchApi';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';

import {
    manageEntitlementsControlHeader,
    manageEntitlementsControlHeaderSticky,
    manageEntitlementsLoadingContainer,
    manageEntitlementsToolRibbonSticky,
} from '../../manage-entitlements.module.styl';
import {useEntitlementsTransfer} from '../roles-grants-resources';

import {DatasetEntitlementsPagination} from './components/dataset-entitlements-pagination';
import {DatasetResourceNameHeader} from './components/dataset-resource-name.header';
import {
    NO_ASSIGNED_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_ENTITLEMENT_TITLE,
    SECTION_NAME,
    SELECTION_KEY,
} from './constants';
import {useGetDatasetEntitlements} from './hooks/useGetDatasetEntitlements';
import {datasetEntitlementsStateActions} from './slices/dataset-entitlements-state.slice';
import {datasetEntitlementsActions} from './slices/dataset-entitlements.slice';

import type {ResourceView} from 'src/modules/entitlements/typedef';

const EMPTY_ROWS: ResourceView[] = [];

export const DatasetEntitlementsTab = () => {
    const dispatch = useDispatch();

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys} = useGetDatasetEntitlements();
    const {
        disabled: isRolesDisabled,
        isExpanded: isRolesExpanded,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    const {
        exportErrorMessage,
        importButton,
        renderExportButton,
        transferModals,
    } = useEntitlementsTransfer({rows: result?.data || EMPTY_ROWS, selectionKey: SELECTION_KEY});

    const {openResourceDrawer, openGrantDrawer, openRoleDrawer} = useOpenEntitlementDrawer();

    useEffect(() => {
        return () => {
            dispatch(closeDrawer());
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(datasetEntitlementsActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filterResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(datasetEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const allowedResourceIds = useMemo(() => {
        if (!result?.data) {
            return undefined;
        }
        const ids = new Set<string>();
        result.data.forEach(row => {
            const record = JSON.parse(row.recordId) as {resourceId?: string};
            if (record.resourceId) {
                ids.add(record.resourceId);
            }
        });
        return ids;
    }, [result?.data]);

    const noResultMessage = useMemo(() => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <NoResultPage
                    title="No resources found for the selected criteria."
                    description=""
                />
            );
        }

        if (result && !result.data.length) {
            return (
                <NoResultPage
                    title={NO_ASSIGNED_ENTITLEMENT_TITLE}
                    description={NO_ASSIGNED_ENTITLEMENT_DESCRIPTION}
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    return (
        <>
            <ServerResponseHandler serverError={loadingError} sectionName={SECTION_NAME}>
                <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                    <StickyElement className={`manage-controls ${manageEntitlementsControlHeaderSticky}`}>
                        <div className={manageEntitlementsControlHeader}>
                            <Group justify="space-between">
                                <ResourceSuggest
                                    onSearch={filterResources}
                                    id="dataset-resource-suggest"
                                    allowedResourceIds={allowedResourceIds}
                                    searchAction={searchDatasetResources}
                                />
                                <Group>
                                    <Group alignItems="center">
                                        <Label>Expand Roles</Label>
                                        <StateButton
                                            {...{
                                                name: 'dataset-expand-roles',
                                                disabled: isRolesDisabled,
                                                checked: isRolesExpanded,
                                                onChange: toggleRolesExpansion,
                                            }}
                                        />
                                    </Group>
                                </Group>
                            </Group>
                        </div>
                    </StickyElement>
                    {exportErrorMessage}
                    <BoxLoading loading={!result}>
                        <div className={manageEntitlementsLoadingContainer}>
                            {result?.data.length ? (
                                <PreparedEntitlementsView
                                    resourceFilter={(cacheKeys.filters.resourceQuery as string | undefined) || ''}
                                    dataTableId="dataset-account-entitlements"
                                    rows={result.data}
                                    selectionKey={SELECTION_KEY}
                                    customOffset={193}
                                    expandedRoleIds={expandedRoleIds}
                                    setExpandedRoleIds={setExpandedRoleIds}
                                    toolRibbonClassName={manageEntitlementsToolRibbonSticky}
                                    filtersTools={importButton}
                                    onOpenResource={openResourceDrawer}
                                    onOpenGrant={openGrantDrawer}
                                    onOpenRole={openRoleDrawer}
                                    getColumnWidthByColumnId={getColumnWidthByColumnId}
                                    onStopResize={onStopResize}
                                    paginationStripe={
                                        <DatasetEntitlementsPagination totalCount={result.totalCount}/>
                                    }
                                    shortHeader={<DatasetResourceNameHeader/>}>
                                    {renderExportButton}
                                </PreparedEntitlementsView>
                            ) : noResultMessage}
                        </div>
                    </BoxLoading>
                    {transferModals}
                </ErrorBoundary>
            </ServerResponseHandler>
        </>
    );
};
